import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import RoleTree from './components/RoleTree';
import PermissionMatrix from './components/PermissionMatrix';
import RoleDetails from './components/RoleDetails';
import CreateRoleModal from './components/CreateRoleModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import { notify } from 'shared/utils/notify';

const RolePermissionManagement = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showPermissionPanel, setShowPermissionPanel] = useState(false);

  // Mock roles data with hierarchy
  const [roles, setRoles] = useState([
    {
      id: 'admin',
      name: 'System Administrator',
      description: 'Full system access with all administrative privileges',
      userCount: 3,
      parentId: null,
      level: 0,
      isExpanded: true,
      permissions: {
        userManagement: { create: true, read: true, update: true, delete: true },
        authentication: { create: true, read: true, update: true, delete: true },
        reporting: { create: true, read: true, update: true, delete: true },
        systemSettings: { create: true, read: true, update: true, delete: true }
      }
    },
    {
      id: 'manager',
      name: 'Department Manager',
      description: 'Departmental user management and reporting access',
      userCount: 8,
      parentId: null,
      level: 0,
      isExpanded: true,
      permissions: {
        userManagement: { create: true, read: true, update: true, delete: false },
        authentication: { create: false, read: true, update: false, delete: false },
        reporting: { create: true, read: true, update: true, delete: false },
        systemSettings: { create: false, read: true, update: false, delete: false }
      }
    },
    {
      id: 'hr-manager',
      name: 'HR Manager',
      description: 'Human resources management with user oversight',
      userCount: 5,
      parentId: 'manager',
      level: 1,
      isExpanded: false,
      permissions: {
        userManagement: { create: true, read: true, update: true, delete: false },
        authentication: { create: false, read: true, update: false, delete: false },
        reporting: { create: true, read: true, update: false, delete: false },
        systemSettings: { create: false, read: false, update: false, delete: false }
      }
    },
    {
      id: 'team-lead',
      name: 'Team Lead',
      description: 'Team management with limited administrative access',
      userCount: 12,
      parentId: 'manager',
      level: 1,
      isExpanded: false,
      permissions: {
        userManagement: { create: false, read: true, update: true, delete: false },
        authentication: { create: false, read: true, update: false, delete: false },
        reporting: { create: true, read: true, update: false, delete: false },
        systemSettings: { create: false, read: false, update: false, delete: false }
      }
    },
    {
      id: 'user',
      name: 'Standard User',
      description: 'Basic user access with profile management',
      userCount: 156,
      parentId: null,
      level: 0,
      isExpanded: false,
      permissions: {
        userManagement: { create: false, read: false, update: false, delete: false },
        authentication: { create: false, read: false, update: false, delete: false },
        reporting: { create: false, read: false, update: false, delete: false },
        systemSettings: { create: false, read: false, update: false, delete: false }
      }
    },
    {
      id: 'guest',
      name: 'Guest User',
      description: 'Limited read-only access for external users',
      userCount: 23,
      parentId: 'user',
      level: 1,
      isExpanded: false,
      permissions: {
        userManagement: { create: false, read: false, update: false, delete: false },
        authentication: { create: false, read: false, update: false, delete: false },
        reporting: { create: false, read: false, update: false, delete: false },
        systemSettings: { create: false, read: false, update: false, delete: false }
      }
    }
  ]);

  // Permission modules configuration
  const permissionModules = [
    {
      id: 'userManagement',
      name: 'User Management',
      description: 'Manage user accounts, profiles, and access',
      icon: 'Users',
      permissions: [
        { key: 'create', label: 'Create Users', description: 'Add new user accounts' },
        { key: 'read', label: 'View Users', description: 'View user information and lists' },
        { key: 'update', label: 'Edit Users', description: 'Modify user profiles and settings' },
        { key: 'delete', label: 'Delete Users', description: 'Remove user accounts' }
      ]
    },
    {
      id: 'authentication',
      name: 'Authentication',
      description: 'Manage authentication settings and policies',
      icon: 'Shield',
      permissions: [
        { key: 'create', label: 'Create Auth Policies', description: 'Define authentication rules' },
        { key: 'read', label: 'View Auth Logs', description: 'Access authentication logs' },
        { key: 'update', label: 'Update Auth Settings', description: 'Modify authentication configuration' },
        { key: 'delete', label: 'Delete Auth Policies', description: 'Remove authentication rules' }
      ]
    },
    {
      id: 'reporting',
      name: 'Reporting',
      description: 'Generate and manage system reports',
      icon: 'BarChart3',
      permissions: [
        { key: 'create', label: 'Create Reports', description: 'Generate new reports' },
        { key: 'read', label: 'View Reports', description: 'Access existing reports' },
        { key: 'update', label: 'Edit Reports', description: 'Modify report configurations' },
        { key: 'delete', label: 'Delete Reports', description: 'Remove reports' }
      ]
    },
    {
      id: 'systemSettings',
      name: 'System Settings',
      description: 'Configure system-wide settings and preferences',
      icon: 'Settings',
      permissions: [
        { key: 'create', label: 'Create Settings', description: 'Add new system configurations' },
        { key: 'read', label: 'View Settings', description: 'Access system configuration' },
        { key: 'update', label: 'Update Settings', description: 'Modify system settings' },
        { key: 'delete', label: 'Delete Settings', description: 'Remove system configurations' }
      ]
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (roles.length > 0 && !selectedRole) {
      setSelectedRole(roles[0]);
    }
  }, [roles]);

  const handleRoleSelect = (role) => {
    if (hasUnsavedChanges) {
      if (window.confirm('У вас есть несохраненные изменения. Продолжить без сохранения?')) {
        setSelectedRole(role);
        setHasUnsavedChanges(false);
        if (isMobileView) {
          setShowPermissionPanel(true);
        }
      }
    } else {
      setSelectedRole(role);
      if (isMobileView) {
        setShowPermissionPanel(true);
      }
    }
  };

  const handlePermissionChange = (moduleId, permissionKey, value) => {
    if (!selectedRole) return;

    const updatedRoles = roles.map(role => {
      if (role.id === selectedRole.id) {
        const updatedRole = {
          ...role,
          permissions: {
            ...role.permissions,
            [moduleId]: {
              ...role.permissions[moduleId],
              [permissionKey]: value
            }
          }
        };
        setSelectedRole(updatedRole);
        return updatedRole;
      }
      return role;
    });

    setRoles(updatedRoles);
    setHasUnsavedChanges(true);
  };

  const handleSaveChanges = () => {
    // In real app, this would make API call
    console.log('Saving role permissions:', selectedRole);
    setHasUnsavedChanges(false);
    notify.success('Изменения роли сохранены');
  };

  const handleCreateRole = (newRole) => {
    const roleWithId = {
      ...newRole,
      id: `role-${Date.now()}`,
      userCount: 0,
      level: newRole.parentId ? 1 : 0,
      isExpanded: false,
      permissions: {
        userManagement: { create: false, read: false, update: false, delete: false },
        authentication: { create: false, read: false, update: false, delete: false },
        reporting: { create: false, read: false, update: false, delete: false },
        systemSettings: { create: false, read: false, update: false, delete: false }
      }
    };

    setRoles([...roles, roleWithId]);
    setSelectedRole(roleWithId);
    setIsCreateModalOpen(false);
    notify.success(`Роль «${newRole.name}» добавлена`);
  };

  const handleCloneRole = () => {
    if (!selectedRole) return;

    const clonedRole = {
      ...selectedRole,
      id: `${selectedRole.id}-clone-${Date.now()}`,
      name: `${selectedRole.name} (Copy)`,
      userCount: 0
    };

    setRoles([...roles, clonedRole]);
    setSelectedRole(clonedRole);
    notify.success(`Роль «${clonedRole.name}» скопирована`);
  };

  const handleDeleteRole = (role) => {
    setRoleToDelete(role);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteRole = () => {
    if (!roleToDelete) return;

    const updatedRoles = roles.filter(role => role.id !== roleToDelete.id);
    const deletedName = roleToDelete.name;
    setRoles(updatedRoles);
    
    if (selectedRole?.id === roleToDelete.id) {
      setSelectedRole(updatedRoles[0] || null);
    }
    
    setIsDeleteModalOpen(false);
    setRoleToDelete(null);
    notify.success(`Роль «${deletedName}» удалена`);
  };

  const handleBulkPermissionAssignment = (moduleId, permissions) => {
    if (!selectedRole) return;

    const updatedRoles = roles.map(role => {
      if (role.id === selectedRole.id) {
        const updatedRole = {
          ...role,
          permissions: {
            ...role.permissions,
            [moduleId]: permissions
          }
        };
        setSelectedRole(updatedRole);
        return updatedRole;
      }
      return role;
    });

    setRoles(updatedRoles);
    setHasUnsavedChanges(true);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="bg-surface border-b border-secondary-200 px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <span>Dashboard</span>
          <Icon name="ChevronRight" size={16} />
          <span>Administration</span>
          <Icon name="ChevronRight" size={16} />
          <span className="text-text-primary font-medium">Roles & Permissions</span>
        </div>
      </div>

      {/* Header */}
      <div className="bg-surface border-b border-secondary-200 px-6 py-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-text-primary mb-2">
              Role & Permission Management
            </h1>
            <p className="text-text-secondary">
              Manage user roles and define granular permissions across the system
            </p>
          </div>

          <div className="flex items-center gap-3">
            {hasUnsavedChanges && (
              <div className="flex items-center gap-2 text-warning text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>Unsaved changes</span>
              </div>
            )}
            
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="
                bg-primary text-white px-4 py-2 rounded-md font-medium
                hover:bg-primary-700 transition-colors duration-150 hover-lift
                focus:outline-none focus:ring-2 focus:ring-primary/20
                flex items-center gap-2
              "
            >
              <Icon name="Plus" size={16} />
              Create Role
            </button>

            {selectedRole && (
              <>
                <button
                  onClick={handleCloneRole}
                  className="
                    border border-secondary-300 text-text-primary px-4 py-2 rounded-md font-medium
                    hover:bg-secondary-50 transition-colors duration-150 hover-lift
                    focus:outline-none focus:ring-2 focus:ring-primary/20
                    flex items-center gap-2
                  "
                >
                  <Icon name="Copy" size={16} />
                  Clone
                </button>

                <button
                  onClick={handleSaveChanges}
                  disabled={!hasUnsavedChanges}
                  className={`
                    px-4 py-2 rounded-md font-medium transition-colors duration-150 hover-lift
                    focus:outline-none focus:ring-2 focus:ring-primary/20
                    flex items-center gap-2
                    ${hasUnsavedChanges
                      ? 'bg-success text-white hover:bg-success-600' :'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                    }
                  `}
                >
                  <Icon name="Save" size={16} />
                  Save Changes
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 lg:flex">
        {/* Left Panel - Role Tree */}
        <div className={`
          lg:w-1/3 bg-surface border-r border-secondary-200
          ${isMobileView && showPermissionPanel ? 'hidden' : 'block'}
        `}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-text-primary">Role Hierarchy</h2>
              <div className="text-sm text-text-secondary">
                {roles.length} roles
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <Icon 
                name="Search" 
                size={16} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" 
              />
              <input
                type="text"
                placeholder="Search roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="
                  w-full pl-10 pr-4 py-2 border border-secondary-200 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                  transition-all duration-150 text-sm
                "
              />
            </div>

            <RoleTree
              roles={roles}
              selectedRole={selectedRole}
              searchTerm={searchTerm}
              onRoleSelect={handleRoleSelect}
              onDeleteRole={handleDeleteRole}
            />
          </div>
        </div>

        {/* Right Panel - Permission Matrix */}
        <div className={`
          lg:w-2/3 bg-background
          ${isMobileView && !showPermissionPanel ? 'hidden' : 'block'}
        `}>
          {isMobileView && (
            <div className="bg-surface border-b border-secondary-200 p-4">
              <button
                onClick={() => setShowPermissionPanel(false)}
                className="flex items-center gap-2 text-text-secondary hover:text-text-primary"
              >
                <Icon name="ArrowLeft" size={16} />
                Back to Roles
              </button>
            </div>
          )}

          {selectedRole ? (
            <div className="p-6">
              <RoleDetails
                role={selectedRole}
                onDeleteRole={() => handleDeleteRole(selectedRole)}
              />

              <PermissionMatrix
                role={selectedRole}
                permissionModules={permissionModules}
                onPermissionChange={handlePermissionChange}
                onBulkAssignment={handleBulkPermissionAssignment}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Shield" size={32} className="text-secondary-400" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  Select a Role
                </h3>
                <p className="text-text-secondary">
                  Choose a role from the left panel to view and edit its permissions
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateRoleModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateRole={handleCreateRole}
        existingRoles={roles}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeleteRole}
        role={roleToDelete}
      />
    </div>
  );
};

export default RolePermissionManagement;